import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, User, Building2, Phone, Mail, MapPin, 
  Calendar, FileText, CheckCircle, XCircle, Clock,
  Edit, Trash2, Loader2, Eye, AlertTriangle, TrendingUp,
  Hash, ClipboardList, Briefcase, CreditCard, Home, Map,
  Save, X, FileImage, Download, ExternalLink, Image,
  Camera, RefreshCw, Upload
} from 'lucide-react';
import Swal from 'sweetalert2';
import Webcam from 'react-webcam';

function InspectionDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const [trader, setTrader] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingInspection, setEditingInspection] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  
  // Camera states for inspection photos only
  const [showCamera, setShowCamera] = useState(false);
  const [cameraType, setCameraType] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const webcamRef = useRef(null);
  
  // State for inspection form
  const [inspectionFormData, setInspectionFormData] = useState({
    commodity_checked: '',
    violation_type: '',
    action_taken: '',
    status: 'pending',
    photo_evidence: null,
    photo_license: null
  });

  const [editFormData, setEditFormData] = useState({
    violation_type: '',
    action_taken: '',
    status: ''
  });

  const [editTraderData, setEditTraderData] = useState({
    name: '',
    tin: '',
    email: '',
    phone: '',
    business_name: '',
    business_license: '',
    city: '',
    subcity: '',
    woreda: '',
    address: '',
    region: '',
    status: ''
  });

  const violationTypeOptions = [
    { value: '', label: '-- Please Select Violation Type --' },
    { value: 'expired_product', label: 'Expired Product' },
    { value: 'price_violation', label: 'Price Violation' },
    { value: 'no_license', label: 'Trading Without License' },
    { value: 'short_weight', label: 'Short Weight' },
    { value: 'counterfeit', label: 'Counterfeit Product' },
    { value: 'unsafe_product', label: 'Unsafe Product' },
    { value: 'false_advertising', label: 'False Advertising' },
    { value: 'tax_evasion', label: 'Tax Evasion' },
    { value: 'other', label: 'Other Violation' }
  ];

  const actionTakenOptions = [
    { value: '', label: '-- Please Select Action Taken --' },
    { value: 'verbal_warning', label: 'Verbal Warning' },
    { value: 'written_warning', label: 'Written Warning' },
    { value: 'penalty_issued', label: 'Penalty Issued' },
    { value: 'product_seized', label: 'Product Seized' },
    { value: 'license_suspended', label: 'License Suspended' },
    { value: 'business_closed', label: 'Business Closed' },
    { value: 'no_action', label: 'No Action' }
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' }
  ];

  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
    headers: { 
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    withCredentials: true,
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Remove Content-Type for FormData to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  });

  // Helper: Convert data URL to File
  const dataURLToFile = (dataURL, filename) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  // Capture photo from webcam
  const capturePhoto = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      setCapturedImage(imageSrc);
    }
  }, [webcamRef]);

  // Save captured photo to form data
  const saveCapturedPhoto = () => {
    if (!capturedImage) return;
    
    const filename = `${cameraType}_${Date.now()}.jpg`;
    const file = dataURLToFile(capturedImage, filename);
    
    if (cameraType === 'evidence') {
      setInspectionFormData(prev => ({
        ...prev,
        photo_evidence: file
      }));
    } else if (cameraType === 'license') {
      setInspectionFormData(prev => ({
        ...prev,
        photo_license: file
      }));
    }
    
    Swal.fire({
      icon: 'success',
      title: 'Photo Captured!',
      text: `${cameraType === 'evidence' ? 'Evidence' : 'License'} photo has been captured`,
      timer: 1500
    });
    
    setShowCamera(false);
    setCapturedImage(null);
    setCameraType(null);
  };

  const retakePhoto = () => {
    setCapturedImage(null);
  };

  const closeCamera = () => {
    setShowCamera(false);
    setCapturedImage(null);
    setCameraType(null);
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    if (path.startsWith('data:')) return path;
    
    let filename = path;
    if (path.includes('/')) {
      filename = path.split('/').pop();
    }
    if (path.includes('\\')) {
      filename = path.split('\\').pop();
    }
    
    return `http://localhost:8000/Trader/${filename}`;
  };

  const openPhotoModal = (photoUrl, photoType) => {
    setSelectedPhoto({ url: photoUrl, type: photoType });
    setIsPhotoModalOpen(true);
  };

  const removePhoto = (type) => {
    if (type === 'evidence') {
      setInspectionFormData(prev => ({ ...prev, photo_evidence: null }));
    } else if (type === 'license') {
      setInspectionFormData(prev => ({ ...prev, photo_license: null }));
    }
  };

  useEffect(() => {
    if (location.state?.data) {
      const responseData = location.state.data;
      
      if (responseData.data) {
        setTrader(responseData.data.trader || responseData.data);
        setInspections(responseData.data.inspections || []);
      } else if (responseData.trader) {
        setTrader(responseData.trader);
        setInspections(responseData.inspections || []);
      } else {
        setTrader(responseData);
        setInspections([]);
      }
      setLoading(false);
    } else {
      const fetchData = async () => {
        try {
          const id = window.location.pathname.split('/').pop();
          const response = await api.get(`/inspection/${id}`);
          if (response.data.success) {
            setTrader(response.data.data.trader);
            setInspections(response.data.data.inspections || []);
          } else if (response.data.data) {
            setTrader(response.data.data);
          } else {
            setTrader(response.data);
          }
        } catch (err) {
          console.error('Error fetching details:', err);
          setError('Failed to load inspection details');
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to load trader inspection details'
          });
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [location]);

  const stats = {
    totalInspections: inspections.length,
    completedInspections: inspections.filter(i => i.status === 'completed' || i.status === 'Completed').length,
    pendingInspections: inspections.filter(i => i.status === 'pending' || i.status === 'Pending').length,
    failedInspections: inspections.filter(i => i.status === 'failed' || i.status === 'Failed').length,
    violations: inspections.filter(i => i.violation_type).length,
    complianceRate: inspections.length > 0 
      ? ((inspections.filter(i => i.status === 'completed' || i.status === 'Completed').length / inspections.length) * 100).toFixed(1)
      : 100
  };

  const getStatusColor = (status) => {
    const lowerStatus = status?.toLowerCase();
    switch(lowerStatus) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getComplianceColor = (rate) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const handleInspectionInputChange = (e) => {
    const { name, value } = e.target;
    setInspectionFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const HandleSaveInspection = async () => {
    if (!trader || !trader.id) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Cannot save: Trader information not available'
      });
      return;
    }

    if (!inspectionFormData.commodity_checked) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please enter commodity information'
      });
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      
      formData.append('trader_id', trader.id);
      formData.append('name', trader.name || '');
      formData.append('tin', trader.tin || '');
      formData.append('email', trader.email || '');
      formData.append('phone', trader.phone || '');
      formData.append('business_name', trader.business_name || '');
      formData.append('business_license', trader.business_license || '');
      formData.append('city', trader.city || '');
      formData.append('subcity', trader.subcity || '');
      formData.append('woreda', trader.woreda || '');
      formData.append('region', trader.region || '');
      formData.append('address', trader.address || '');
      formData.append('latitude', trader.latitude || '');
      formData.append('longitude', trader.longitude || '');
      
      formData.append('commodity_checked', inspectionFormData.commodity_checked);
      formData.append('violation_type', inspectionFormData.violation_type || '');
      formData.append('action_taken', inspectionFormData.action_taken || '');
      formData.append('status', inspectionFormData.status);
      formData.append('inspection_date', new Date().toISOString());
      formData.append('quantity_checked', '1');
      formData.append('unit', 'unit');
      
      if (inspectionFormData.photo_evidence instanceof File) {
        formData.append('photo_evidence', inspectionFormData.photo_evidence);
      }
      if (inspectionFormData.photo_license instanceof File) {
        formData.append('photo_license', inspectionFormData.photo_license);
      }

      const response = await api.post(`/saveinspection/${trader.id}`, formData);
      
      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Saved!',
          text: 'Inspection saved successfully with photos',
          timer: 2000
        });
        
        setInspectionFormData({
          commodity_checked: '',
          violation_type: '',
          action_taken: '',
          status: 'pending',
          photo_evidence: null,
          photo_license: null
        });
        
        await fetchUpdatedTraderData();
      }
    } catch (error) {
      console.error('Error saving:', error);
      
      let errorMessage = 'Failed to save inspection';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message === 'Network Error') {
        errorMessage = 'Network error. Please check if the server is running and CORS is configured properly.';
      }
      
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage
      });
    } finally {
      setIsUploading(false);
    }
  };

  const HandleSaveTrader = async () => {
    if (!trader || !trader.id) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Cannot save: Trader information not available'
      });
      return;
    }

    try {
      const response = await api.post(`/save-trader/${trader.id}`, editTraderData);
      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Saved!',
          text: 'Trader information saved successfully',
          timer: 2000
        });
        setIsEditing(false);
        setTrader({...trader, ...editTraderData});
        await fetchUpdatedTraderData();
      }
    } catch (error) {
      console.error('Error saving trader:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to save trader information'
      });
    }
  };

  const handleEditInspection = (inspection) => {
    setEditingInspection(inspection);
    setEditFormData({
      violation_type: inspection.violation_type || '',
      action_taken: inspection.action_taken || '',
      status: inspection.status || 'pending'
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateInspection = async () => {
    try {
      const response = await api.put(`/inspections/${editingInspection.id}`, editFormData);
      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Updated!',
          text: 'Inspection updated successfully',
          timer: 2000
        });
        setInspections(inspections.map(i => 
          i.id === editingInspection.id ? { ...i, ...editFormData } : i
        ));
        setIsEditModalOpen(false);
        setEditingInspection(null);
      }
    } catch (error) {
      console.error('Error updating inspection:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update inspection'
      });
    }
  };

  const handleBack = () => {
    navigate('/admin/inspection');
  };

  const toggleEdit = () => {
    if (!isEditing) {
      setEditTraderData({
        name: trader?.name || '',
        tin: trader?.tin || '',
        email: trader?.email || '',
        phone: trader?.phone || '',
        business_name: trader?.business_name || '',
        business_license: trader?.business_license || '',
        city: trader?.city || '',
        subcity: trader?.subcity || '',
        woreda: trader?.woreda || '',
        region: trader?.region || '',
        address: trader?.address || '',
        status: trader?.status || ''
      });
    }
    setIsEditing(!isEditing);
  };

  const fetchUpdatedTraderData = async () => {
    try {
      const response = await api.get(`/inspection/${trader.id}`);
      if (response.data.success) {
        setTrader(response.data.data.trader);
        setInspections(response.data.data.inspections || []);
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  const handleEditTraderChange = (e) => {
    const { name, value } = e.target;
    setEditTraderData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading inspection details...</p>
        </div>
      </div>
    );
  }

  if (error || !trader) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-20 h-20 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Data</h2>
          <p className="text-gray-500 mb-6">{error || 'Trader information not found'}</p>
          <button
            onClick={handleBack}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition"
          >
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 p-4 sm:p-6 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 mb-4 transition"
          >
            <ArrowLeft size={20} />
            Back to Search
          </button>
          
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">Trader Inspection Details</h1>
                  <p className="text-indigo-100 text-sm">Complete inspection history and trader information</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  trader.status === 'active' ? 'bg-green-500 text-white' :
                  trader.status === 'pending' ? 'bg-yellow-500 text-white' :
                  trader.status === 'inactive' ? 'bg-red-500 text-white' :
                  'bg-gray-500 text-white'
                }`}>
                  {trader.status || 'Active'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-indigo-500 hover:shadow-xl transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-700 text-sm font-medium">Total Inspections</p>
                <p className="text-2xl font-bold text-gray-800">{stats.totalInspections}</p>
              </div>
              <ClipboardList className="w-10 h-10 text-indigo-500 opacity-50" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-indigo-500 hover:shadow-xl transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-700 text-sm font-medium">Compliance Rate</p>
                <p className={`text-2xl font-bold ${getComplianceColor(stats.complianceRate)}`}>
                  {stats.complianceRate}%
                </p>
              </div>
              <TrendingUp className="w-10 h-10 text-indigo-500 opacity-50" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-indigo-500 hover:shadow-xl transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-700 text-sm font-medium">Pending Inspections</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pendingInspections}</p>
              </div>
              <Clock className="w-10 h-10 text-indigo-500 opacity-50" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-indigo-500 hover:shadow-xl transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-700 text-sm font-medium">Violations Found</p>
                <p className="text-2xl font-bold text-red-600">{stats.violations}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-indigo-500 opacity-50" />
            </div>
          </div>
        </div>

        {/* Add New Inspection Section */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
          <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
            <h2 className="text-lg font-bold text-indigo-700 flex items-center gap-2">
              <FileText size={20} className="text-indigo-600" />
              Add New Inspection
            </h2>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-indigo-700 text-sm font-medium mb-2">Commodity Checked *</label>
                <input 
                  type="text" 
                  name="commodity_checked"
                  value={inspectionFormData.commodity_checked}
                  onChange={handleInspectionInputChange}
                  placeholder="Enter commodity name"
                  className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-indigo-700 text-sm font-medium mb-2">Violation Type</label>
                <select 
                  name="violation_type"
                  value={inspectionFormData.violation_type}
                  onChange={handleInspectionInputChange}
                  className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  {violationTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-indigo-700 text-sm font-medium mb-2">Action Taken</label>
                <select 
                  name="action_taken"
                  value={inspectionFormData.action_taken}
                  onChange={handleInspectionInputChange}
                  className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  {actionTakenOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-indigo-700 text-sm font-medium mb-2">Status</label>
                <select 
                  name="status"
                  value={inspectionFormData.status}
                  onChange={handleInspectionInputChange}
                  className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Evidence and License Photo Capture */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div>
                <label className="block text-indigo-700 text-sm font-medium mb-2">Evidence Photo</label>
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => {
                      setCameraType('evidence');
                      setShowCamera(true);
                      setCapturedImage(null);
                    }}
                    className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
                  >
                    <Camera size={14} />
                    Take Evidence Photo
                  </button>
                  {inspectionFormData.photo_evidence && (
                    <button
                      type="button"
                      onClick={() => removePhoto('evidence')}
                      className="px-3 py-1.5 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {inspectionFormData.photo_evidence && (
                  <div className="mt-2">
                    <img 
                      src={inspectionFormData.photo_evidence instanceof File ? URL.createObjectURL(inspectionFormData.photo_evidence) : getImageUrl(inspectionFormData.photo_evidence)}
                      alt="Evidence Preview"
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                    <p className="text-xs text-green-600 mt-1">✓ Evidence photo captured</p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-indigo-700 text-sm font-medium mb-2">License Photo</label>
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => {
                      setCameraType('license');
                      setShowCamera(true);
                      setCapturedImage(null);
                    }}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition flex items-center gap-2"
                  >
                    <Camera size={14} />
                    Take License Photo
                  </button>
                  {inspectionFormData.photo_license && (
                    <button
                      type="button"
                      onClick={() => removePhoto('license')}
                      className="px-3 py-1.5 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {inspectionFormData.photo_license && (
                  <div className="mt-2">
                    <img 
                      src={inspectionFormData.photo_license instanceof File ? URL.createObjectURL(inspectionFormData.photo_license) : getImageUrl(inspectionFormData.photo_license)}
                      alt="License Preview"
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                    <p className="text-xs text-green-600 mt-1">✓ License photo captured</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={HandleSaveInspection}
                disabled={isUploading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2 disabled:opacity-50"
              >
                {isUploading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {isUploading ? 'Saving...' : 'Save Inspection'}
              </button>
            </div>
          </div>
        </div>

        {/* Camera Modal */}
        {showCamera && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
            <div className="relative max-w-2xl w-full">
              <button
                onClick={closeCamera}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 transition"
              >
                <X size={32} />
              </button>
              
              <div className="bg-white rounded-xl overflow-hidden">
                <div className="p-4 bg-gray-100 border-b">
                  <h3 className="text-lg font-semibold text-gray-800">
                    {cameraType === 'evidence' ? 'Take Evidence Photo' : 'Take License Photo'}
                  </h3>
                  <p className="text-sm text-gray-500">Trader: {trader?.name}</p>
                </div>
                
                <div className="p-4">
                  {!capturedImage ? (
                    <Webcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      screenshotQuality={0.8}
                      className="w-full rounded-lg"
                      videoConstraints={{
                        facingMode: "environment"
                      }}
                    />
                  ) : (
                    <img
                      src={capturedImage}
                      alt="Captured"
                      className="w-full rounded-lg"
                    />
                  )}
                </div>
                
                <div className="p-4 bg-gray-50 flex justify-center gap-4">
                  {!capturedImage ? (
                    <button
                      onClick={capturePhoto}
                      className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
                    >
                      <Camera size={20} />
                      Capture Photo
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={retakePhoto}
                        className="px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition flex items-center gap-2"
                      >
                        <RefreshCw size={20} />
                        Retake
                      </button>
                      <button
                        onClick={saveCapturedPhoto}
                        className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
                      >
                        <Upload size={20} />
                        Use This Photo
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Trader Information Section - ONLY Business License and National ID Photos */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
          <div className="border-b border-gray-200 px-6 py-4 bg-gray-50 flex justify-between items-center">
            <h2 className="text-lg font-bold text-indigo-700 flex items-center gap-2">
              <User size={20} className="text-indigo-600" />
              Trader Information
            </h2>
            <div className="flex gap-2">
              <button
                onClick={toggleEdit}
                className="text-indigo-600 hover:text-indigo-700 text-sm flex items-center gap-1"
              >
                <Edit size={16} />
                {isEditing ? 'Cancel' : 'Edit'}
              </button>
            </div>
          </div>
          
          <div className="overflow-x-auto p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-indigo-700 text-sm font-medium mb-2">Full Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="name"
                    value={editTraderData.name}
                    onChange={handleEditTraderChange}
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 font-semibold">{trader.name || trader.fullname || '—'}</p>
                )}
              </div>
              
              <div>
                <label className="block text-indigo-700 text-sm font-medium mb-2">TIN Number</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="tin"
                    value={editTraderData.tin}
                    onChange={handleEditTraderChange}
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 font-mono">{trader.tin || '—'}</p>
                )}
              </div>
              
              <div>
                <label className="block text-indigo-700 text-sm font-medium mb-2">Email Address</label>
                {isEditing ? (
                  <input
                    type="email"
                    name="email"
                    value={editTraderData.email}
                    onChange={handleEditTraderChange}
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 flex items-center gap-2">
                    <Mail size={14} className="text-gray-400" />
                    {trader.email || '—'}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-indigo-700 text-sm font-medium mb-2">Phone Number</label>
                {isEditing ? (
                  <input
                    type="tel"
                    name="phone"
                    value={editTraderData.phone}
                    onChange={handleEditTraderChange}
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 flex items-center gap-2">
                    <Phone size={14} className="text-gray-400" />
                    {trader.phone || trader.phone_number || '—'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-indigo-700 text-sm font-medium mb-2">Business Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="business_name"
                    value={editTraderData.business_name}
                    onChange={handleEditTraderChange}
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 flex items-center gap-2">
                    <Briefcase size={14} className="text-gray-400" />
                    {trader.business_name || trader.company_name || '—'}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-indigo-700 text-sm font-medium mb-2">Business License #</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="business_license"
                    value={editTraderData.business_license}
                    onChange={handleEditTraderChange}
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 flex items-center gap-2">
                    <CreditCard size={14} className="text-gray-400" />
                    {trader.business_license || trader.license_number || '—'}
                  </p>
                )}
              </div>

              {/* ONLY Business License and National ID Photos Section */}
              <div className="md:col-span-2">
                <label className="block text-indigo-700 text-sm font-medium mb-3">Documents / Photos</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Business License Photo */}
                  <div className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Business License</span>
                      {trader.license_photo_path && (
                        <button
                          onClick={() => openPhotoModal(getImageUrl(trader.license_photo_path), 'Business License')}
                          className="text-indigo-600 hover:text-indigo-700 text-xs flex items-center gap-1"
                        >
                          <ExternalLink size={14} />
                          View Full
                        </button>
                      )}
                    </div>
                    {trader.license_photo_path ? (
                      <button
                        onClick={() => openPhotoModal(getImageUrl(trader.license_photo_path), 'Business License')}
                        className="w-full"
                      >
                        <img 
                          src={getImageUrl(trader.license_photo_path)} 
                          alt="Business License"
                          className="w-full h-40 object-cover rounded-lg border border-gray-200 hover:border-indigo-500 transition"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/400x200?text=License+Not+Found';
                          }}
                        />
                      </button>
                    ) : (
                      <div className="w-full h-40 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center">
                        <FileImage className="w-10 h-10 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-400">No license photo uploaded</p>
                      </div>
                    )}
                  </div>
                  
                  {/* National ID Photo */}
                  <div className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">National ID</span>
                      {trader.national_id_photo_path && (
                        <button
                          onClick={() => openPhotoModal(getImageUrl(trader.national_id_photo_path), 'National ID')}
                          className="text-indigo-600 hover:text-indigo-700 text-xs flex items-center gap-1"
                        >
                          <ExternalLink size={14} />
                          View Full
                        </button>
                      )}
                    </div>
                    {trader.national_id_photo_path ? (
                      <button
                        onClick={() => openPhotoModal(getImageUrl(trader.national_id_photo_path), 'National ID')}
                        className="w-full"
                      >
                        <img 
                          src={getImageUrl(trader.national_id_photo_path)} 
                          alt="National ID"
                          className="w-full h-40 object-cover rounded-lg border border-gray-200 hover:border-indigo-500 transition"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/400x200?text=ID+Not+Found';
                          }}
                        />
                      </button>
                    ) : (
                      <div className="w-full h-40 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center">
                        <FileImage className="w-10 h-10 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-400">No ID photo uploaded</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-indigo-700 text-sm font-medium mb-2">Region</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="region"
                    value={editTraderData.region}
                    onChange={handleEditTraderChange}
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 flex items-center gap-2">
                    <MapPin size={14} className="text-gray-400" />
                    {trader.region || '—'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-indigo-700 text-sm font-medium mb-2">City</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="city"
                    value={editTraderData.city}
                    onChange={handleEditTraderChange}
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 flex items-center gap-2">
                    <Home size={14} className="text-gray-400" />
                    {trader.city || '—'}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-indigo-700 text-sm font-medium mb-2">Subcity</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="subcity"
                    value={editTraderData.subcity}
                    onChange={handleEditTraderChange}
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">{trader.subcity || '—'}</p>
                )}
              </div>
              
              <div>
                <label className="block text-indigo-700 text-sm font-medium mb-2">Woreda</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="woreda"
                    value={editTraderData.woreda}
                    onChange={handleEditTraderChange}
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">{trader.woreda || '—'}</p>
                )}
              </div>

              <div>
                <label className="block text-indigo-700 text-sm font-medium mb-2">Address</label>
                {isEditing ? (
                  <textarea
                    name="address"
                    value={editTraderData.address}
                    onChange={handleEditTraderChange}
                    rows="2"
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 flex items-start gap-2">
                    <MapPin size={14} className="text-gray-400 mt-0.5" />
                    <span>{trader.address || trader.location || '—'}</span>
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-indigo-700 text-sm font-medium mb-2">Registration Date</label>
                <p className="text-gray-900 flex items-center gap-2">
                  <Calendar size={14} className="text-gray-400" />
                  {trader.created_at ? new Date(trader.created_at).toLocaleDateString() : '—'}
                </p>
              </div>
            </div>
            
            {isEditing && (
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition flex items-center gap-2"
                >
                  <X size={16} />
                  Cancel
                </button>
                <button
                  onClick={HandleSaveTrader}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
                >
                  <Save size={16} />
                  Save Trader Changes
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Inspection History Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
            <h2 className="text-lg font-bold text-indigo-700 flex items-center gap-2">
              <Eye size={20} className="text-indigo-600" />
              Inspection History
            </h2>
          </div>
          
          {inspections.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No inspection records found for this trader</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-indigo-700 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-indigo-700 uppercase tracking-wider">Commodity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-indigo-700 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-indigo-700 uppercase tracking-wider">Violation</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-indigo-700 uppercase tracking-wider">Photos</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-indigo-700 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {inspections.map((inspection, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center gap-1">
                          <Calendar size={14} className="text-indigo-400" />
                          {inspection.inspection_date ? new Date(inspection.inspection_date).toLocaleDateString() : '—'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {inspection.commodity_checked || inspection.commodity || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(inspection.status)}`}>
                          {inspection.status || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {inspection.violation_type ? (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                            {violationTypeOptions.find(opt => opt.value === inspection.violation_type)?.label || inspection.violation_type}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          {inspection.photo_evidence && (
                            <button
                              onClick={() => openPhotoModal(getImageUrl(inspection.photo_evidence), 'Evidence Photo')}
                              className="p-1.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition"
                              title="View Evidence"
                            >
                              <Camera size={14} />
                            </button>
                          )}
                          {inspection.photo_license && (
                            <button
                              onClick={() => openPhotoModal(getImageUrl(inspection.photo_license), 'License Photo')}
                              className="p-1.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition"
                              title="View License"
                            >
                              <FileImage size={14} />
                            </button>
                          )}
                          {!inspection.photo_evidence && !inspection.photo_license && (
                            <span className="text-gray-400 text-xs">No photos</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleEditInspection(inspection)}
                          className="text-indigo-600 hover:text-indigo-800 transition"
                        >
                          <Edit size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleBack}
            className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
          >
            Close
          </button>
        </div>
      </div>

      {/* Edit Inspection Modal */}
      {isEditModalOpen && editingInspection && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-indigo-700 mb-4">Edit Inspection</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-indigo-700 text-sm font-medium mb-2">Violation Type</label>
                  <select
                    value={editFormData.violation_type}
                    onChange={(e) => setEditFormData({...editFormData, violation_type: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">No Violation</option>
                    {violationTypeOptions.filter(opt => opt.value !== '').map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
               
                <div>
                  <label className="block text-indigo-700 text-sm font-medium mb-2">Action Taken</label>
                  <select
                    value={editFormData.action_taken}
                    onChange={(e) => setEditFormData({...editFormData, action_taken: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    {actionTakenOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-indigo-700 text-sm font-medium mb-2">Status</label>
                  <select
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateInspection}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Photo Modal for Fullscreen View */}
      {isPhotoModalOpen && selectedPhoto && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl w-full">
            <button
              onClick={() => setIsPhotoModalOpen(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition"
            >
              <X size={32} />
            </button>
            <div className="bg-white rounded-xl overflow-hidden">
              <div className="p-4 bg-gray-100 border-b">
                <h3 className="text-lg font-semibold text-gray-800">{selectedPhoto.type}</h3>
                <p className="text-sm text-gray-500">Trader: {trader?.name}</p>
              </div>
              <div className="p-4 flex justify-center">
                <img 
                  src={selectedPhoto.url} 
                  alt={selectedPhoto.type}
                  className="max-w-full max-h-[70vh] object-contain"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                  }}
                />
              </div>
              <div className="p-4 bg-gray-50 flex justify-end gap-3">
                <a
                  href={selectedPhoto.url}
                  download
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                >
                  <Download size={16} />
                  Download
                </a>
                <button
                  onClick={() => setIsPhotoModalOpen(false)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InspectionDetail;